import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { toast } from "sonner";
import { api, errMsg, imgSrc } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { ModelCard } from "@/components/ModelCard";
import { EthicalAgreementDialog } from "@/components/EthicalAgreementDialog";
import { LegalDialog } from "@/components/LegalDialog";
import {
  ShieldCheck, Wallet, Clock, Sparkles, UserCheck, Search, FileKey,
  Camera, Handshake, BadgeDollarSign, ArrowRight, Wand2, Globe, Loader2,
  BadgeCheck, User, Building2, TrendingUp, BellRing, Lock, Activity, RefreshCw,
} from "lucide-react";

const HERO_IMG = "https://customer-assets.emergentagent.com/job_model-passive-income/artifacts/k1cvsv8o_Rachel%20Stichbury%20Campaign%20%2869%20of%2078%29.webp";

const DEMO_STAGES = [
  "Reading your website…",
  "Learning your brand's style…",
  "Styling Rachel Stichbury's digital twin…",
  "Draping the garments — fabric, shadows, proportions…",
  "Applying the ethical licence watermark…",
];

const EthicalBadge = ({ className = "" }) => (
  <div
    data-testid="ethical-ai-badge"
    className={`inline-flex items-center gap-2 rounded-full border border-white/30 bg-black/55 px-3.5 py-2 text-xs font-semibold text-white backdrop-blur-md ${className}`}
  >
    <span className="flex h-4 w-4 items-center justify-center rounded-full bg-[hsl(174,38%,42%)]">
      <BadgeCheck className="h-3 w-3 text-white" />
    </span>
    Ethically sourced human-look, generated by AI
  </div>
);

const Section = ({ children, className = "" }) => {
  const reduce = useReducedMotion();
  return (
    <motion.section
      initial={reduce ? false : { opacity: 0, y: 10 }}
      whileInView={reduce ? false : { opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.section>
  );
};

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [featured, setFeatured] = useState([]);
  // Live demo state: idle -> choose -> client|model ; client: generating -> done
  const [demoPath, setDemoPath] = useState("idle");
  const [website, setWebsite] = useState("");
  const [generating, setGenerating] = useState(false);
  const [stageIdx, setStageIdx] = useState(0);
  const [demoResult, setDemoResult] = useState(null);
  const stageTimer = useRef(null);

  useEffect(() => {
    api.get("/models?limit=4").then(({ data }) => setFeatured(data.slice(0, 4))).catch(() => {});
    return () => stageTimer.current && clearInterval(stageTimer.current);
  }, []);

  const runDemo = async (e) => {
    e.preventDefault();
    if (!website.trim()) return toast.error("Enter your website address first");
    setGenerating(true);
    setDemoResult(null);
    setStageIdx(0);
    stageTimer.current = setInterval(() => setStageIdx((i) => Math.min(i + 1, DEMO_STAGES.length - 1)), 9000);
    try {
      const { data } = await api.post("/demo/generate", { website_url: website.trim() }, { timeout: 280000 });
      setDemoResult(data);
      toast.success(`Here's ${data.brand_name || "your brand"} on LOOKBOOK — featuring Rachel's digital twin`);
    } catch (err) {
      toast.error(errMsg(err, "The demo couldn't run right now — please try again"));
    } finally {
      clearInterval(stageTimer.current);
      setGenerating(false);
    }
  };

  const heroImageSrc = demoResult ? imgSrc({ path: demoResult.image_path }) : HERO_IMG;

  return (
    <div>
      {/* HERO */}
      <div className="hero-mist border-b">
        <div className="page-container grid items-center gap-10 py-14 sm:py-20 lg:grid-cols-2">
          <div className="space-y-6">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[hsl(174,38%,34%)]">
              Ethical AI fashion modelling
            </p>
            <h1 className="font-display text-4xl leading-[0.98] tracking-[-0.02em] sm:text-5xl lg:text-6xl">
              Your look.
              <br />
              Your terms.
              <br />
              Your income.
            </h1>
            <p className="max-w-md text-base text-muted-foreground sm:text-lg">
              LOOKBOOK lets real people licence their image and lend to creative agencies for AI image
              generation — paid upfront, consent-first, and protected by a strict ethical agreement.
            </p>

            <div className="flex flex-wrap gap-3">
              <Button
                data-testid="landing-demo-cta"
                size="lg"
                className="rounded-xl shadow-[0_10px_25px_rgba(30,26,22,0.12)] transition-[box-shadow] hover:shadow-[0_14px_35px_rgba(30,26,22,0.16)]"
                onClick={() => setDemoPath(demoPath === "idle" ? "choose" : "idle")}
              >
                <Wand2 className="mr-1.5 h-4 w-4" /> Let us show you what we do
              </Button>
              <Button
                data-testid="landing-hero-primary-cta"
                size="lg"
                variant="outline"
                className="rounded-xl border bg-card hover:bg-muted"
                onClick={() => navigate("/explore")}
              >
                Explore models <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            </div>

            {/* CHOOSER */}
            {demoPath !== "idle" && (
              <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}
                className="max-w-md space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    data-testid="landing-choose-model"
                    onClick={() => setDemoPath("model")}
                    className={`flex items-center gap-2.5 rounded-xl border p-3 text-left text-sm transition-[border-color,background-color] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                      demoPath === "model" ? "border-[hsl(174,38%,34%)] bg-[hsl(174,38%,95%)]" : "bg-card hover:bg-muted"
                    }`}
                  >
                    <User className="h-4 w-4 shrink-0 text-[hsl(174,38%,34%)]" />
                    <span><span className="block font-semibold">I'm a model</span><span className="block text-[11px] text-muted-foreground">Earn from your look</span></span>
                  </button>
                  <button
                    type="button"
                    data-testid="landing-choose-client"
                    onClick={() => setDemoPath("client")}
                    className={`flex items-center gap-2.5 rounded-xl border p-3 text-left text-sm transition-[border-color,background-color] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                      demoPath === "client" ? "border-[hsl(174,38%,34%)] bg-[hsl(174,38%,95%)]" : "bg-card hover:bg-muted"
                    }`}
                  >
                    <Building2 className="h-4 w-4 shrink-0 text-[hsl(174,38%,34%)]" />
                    <span><span className="block font-semibold">I'm a client</span><span className="block text-[11px] text-muted-foreground">See your brand, styled</span></span>
                  </button>
                </div>

                {/* CLIENT PATH: website -> live generation */}
                {demoPath === "client" && (
                  <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}
                    className="space-y-2 rounded-2xl border bg-card p-4 shadow-sm">
                    {!demoResult ? (
                      <>
                        <p className="text-sm font-semibold">Watch AI style your brand — live</p>
                        <p className="text-xs text-muted-foreground">
                          Drop your website below. Our AI reads your brand's style and generates an e-commerce
                          image on the right — featuring <span className="font-semibold text-foreground">Rachel Stichbury's licensed digital twin</span>.
                        </p>
                        <form onSubmit={runDemo} className="flex gap-2 pt-1">
                          <div className="relative flex-1">
                            <Globe className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                              data-testid="landing-demo-website-input"
                              placeholder="yourbrand.com"
                              value={website}
                              onChange={(e) => setWebsite(e.target.value)}
                              disabled={generating}
                              className="rounded-xl bg-background pl-9"
                            />
                          </div>
                          <Button data-testid="landing-demo-generate-button" type="submit" disabled={generating} className="shrink-0 rounded-xl">
                            {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Generate</>}
                          </Button>
                        </form>
                        {generating && (
                          <p data-testid="landing-demo-stage-text" className="flex items-center gap-2 pt-1 text-xs font-medium text-[hsl(174,38%,30%)]">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" /> {DEMO_STAGES[stageIdx]} <span className="text-muted-foreground">(~1 min)</span>
                          </p>
                        )}
                      </>
                    ) : (
                      <div data-testid="landing-demo-result-panel" className="space-y-2">
                        <p className="flex items-center gap-1.5 text-sm font-semibold">
                          <BadgeCheck className="h-4 w-4 text-[hsl(174,38%,34%)]" /> {demoResult.brand_name || "Your brand"}, styled by LOOKBOOK
                        </p>
                        <p className="text-xs text-muted-foreground">{demoResult.style_summary}</p>
                        <p className="text-[11px] text-muted-foreground">
                          Featuring {demoResult.model_name}'s licensed digital twin — she consented, she gets paid,
                          and this exact flow is what your team gets in the Creation Studio.
                        </p>
                        <div className="flex flex-wrap gap-2 pt-1">
                          <Button data-testid="landing-demo-signup-button" size="sm" className="rounded-xl" onClick={() => navigate("/auth?mode=signup")}>
                            Create a free brand account <ArrowRight className="ml-1 h-3.5 w-3.5" />
                          </Button>
                          <Button data-testid="landing-demo-retry-button" size="sm" variant="outline" className="rounded-xl border bg-card hover:bg-muted"
                            onClick={() => { setDemoResult(null); setWebsite(""); }}>
                            <RefreshCw className="mr-1 h-3.5 w-3.5" /> Try another site
                          </Button>
                        </div>
                      </div>
                    )}
                  </motion.div>
                )}

                {/* MODEL PATH: attention grabber */}
                {demoPath === "model" && (
                  <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}
                    data-testid="landing-model-pitch" className="space-y-3 rounded-2xl border-2 border-[hsl(174,38%,70%)] bg-card p-4 shadow-sm">
                    <p className="font-display text-xl leading-tight">Your face could be earning while you sleep.</p>
                    <ul className="space-y-2 text-xs text-muted-foreground">
                      <li className="flex items-start gap-2"><BadgeDollarSign className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[hsl(174,38%,34%)]" /><span><span className="font-semibold text-foreground">Paid the full licence fee upfront</span> — before a brand makes a single image.</span></li>
                      <li className="flex items-start gap-2"><TrendingUp className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[hsl(174,38%,34%)]" /><span><span className="font-semibold text-foreground">Earn on every generation</span> — each AI image made with your twin pays you again.</span></li>
                      <li className="flex items-start gap-2"><BellRing className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[hsl(174,38%,34%)]" /><span><span className="font-semibold text-foreground">You approve every job</span> — instant notifications, one-tap consent, binding opt-outs.</span></li>
                      <li className="flex items-start gap-2"><Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[hsl(174,38%,34%)]" /><span><span className="font-semibold text-foreground">Your twin is encrypted & locked</span> to LOOKBOOK — never downloadable, revocable anytime.</span></li>
                      <li className="flex items-start gap-2"><Activity className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[hsl(174,38%,34%)]" /><span><span className="font-semibold text-foreground">Watch it happen live</span> — a real-time feed of every image made with your likeness.</span></li>
                    </ul>
                    <Button data-testid="landing-model-signup-button" size="sm" className="rounded-xl" onClick={() => navigate("/auth?mode=signup")}>
                      Start earning — join as a model <ArrowRight className="ml-1 h-3.5 w-3.5" />
                    </Button>
                  </motion.div>
                )}
              </motion.div>
            )}

            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 pt-2 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-[hsl(174,38%,34%)]" /> Consent required for each job</span>
              <span className="flex items-center gap-1.5"><Wallet className="h-3.5 w-3.5 text-[hsl(174,38%,34%)]" /> Models paid upfront</span>
              <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-[hsl(174,38%,34%)]" /> Time-bound licences</span>
            </div>
          </div>

          {/* MAIN IMAGE — becomes the live demo canvas */}
          <div className={`relative ${demoResult || generating ? "block" : "hidden lg:block"}`}>
            <div className="relative overflow-hidden rounded-2xl border shadow-[0_20px_60px_rgba(30,26,22,0.15)]">
              <img
                data-testid="landing-hero-image"
                src={heroImageSrc}
                alt={demoResult ? `${demoResult.brand_name} styled on Rachel Stichbury's digital twin` : "Editorial fashion"}
                className="aspect-[4/5] w-full object-cover"
              />
              {generating && (
                <div data-testid="landing-demo-generating-overlay" className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/45 backdrop-blur-[2px]">
                  <Loader2 className="h-8 w-8 animate-spin text-white" />
                  <p className="max-w-[260px] text-center text-sm font-medium text-white">{DEMO_STAGES[stageIdx]}</p>
                </div>
              )}
              {demoResult && !generating && (
                <div className="absolute inset-x-0 bottom-0 flex justify-center bg-gradient-to-t from-black/60 to-transparent p-4 pt-10">
                  <EthicalBadge />
                </div>
              )}
            </div>
            {!demoResult && !generating && (
              <div className="absolute -bottom-4 -left-4 rounded-xl border bg-card px-4 py-3 shadow-lg">
                <p className="flex items-center gap-2 text-sm font-semibold">
                  <Sparkles className="h-4 w-4 text-[hsl(174,38%,34%)]" /> AI-fair rates
                </p>
                <p className="text-xs text-muted-foreground">Suggested from company size, usage & duration</p>
              </div>
            )}
            {demoResult && !generating && (
              <div className="absolute -bottom-4 -left-4 rounded-xl border bg-card px-4 py-3 shadow-lg">
                <p className="flex items-center gap-2 text-sm font-semibold">
                  <BadgeCheck className="h-4 w-4 text-[hsl(174,38%,34%)]" /> {demoResult.brand_name || "Your brand"} × Rachel Stichbury
                </p>
                <p className="text-xs text-muted-foreground">Generated live from your website · registry-watermarked</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* HOW IT WORKS */}
      <Section className="page-container py-14 sm:py-20">
        <h2 className="font-display text-2xl tracking-[-0.01em] sm:text-3xl">How LOOKBOOK works</h2>
        <p className="mt-2 max-w-lg text-sm text-muted-foreground">Two sides of a fair marketplace — pick yours.</p>
        <Tabs defaultValue="models" className="mt-8">
          <TabsList className="rounded-xl">
            <TabsTrigger data-testid="landing-tab-models" value="models" className="rounded-lg">For models & agencies</TabsTrigger>
            <TabsTrigger data-testid="landing-tab-clients" value="clients" className="rounded-lg">For clients</TabsTrigger>
          </TabsList>
          <TabsContent value="models" className="mt-6">
            <div className="grid gap-4 sm:grid-cols-3">
              {[
                { icon: Camera, title: "Create your profile", text: "Upload a variety of images, describe your features, measurements, strengths, skills and interests." },
                { icon: UserCheck, title: "Consent & negotiate", text: "Review every job. Accept, decline, or counter the offer — nothing happens without your consent." },
                { icon: BadgeDollarSign, title: "Get paid upfront", text: "The full agreed fee lands in your wallet before the client can use your look. Passive income, protected." },
              ].map((s) => (
                <Card key={s.title} className="rounded-xl">
                  <CardContent className="space-y-3 p-6">
                    <s.icon className="h-6 w-6 text-[hsl(174,38%,34%)]" />
                    <h3 className="font-semibold">{s.title}</h3>
                    <p className="text-sm text-muted-foreground">{s.text}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
          <TabsContent value="clients" className="mt-6">
            <div className="grid gap-4 sm:grid-cols-3">
              {[
                { icon: Search, title: "Find the right look", text: "Search by category, features and style. Rent one model or a whole cast for your AI e-commerce app." },
                { icon: Sparkles, title: "AI-screened & priced", text: "Describe the job. Our AI screens it against the ethical agreement and suggests a fair rate for your company size, usage and duration." },
                { icon: FileKey, title: "Licence, time-bound", text: "Pay upfront, get a clear licence with expiry, retouching consent flags, and strict usage terms." },
              ].map((s) => (
                <Card key={s.title} className="rounded-xl">
                  <CardContent className="space-y-3 p-6">
                    <s.icon className="h-6 w-6 text-[hsl(174,38%,34%)]" />
                    <h3 className="font-semibold">{s.title}</h3>
                    <p className="text-sm text-muted-foreground">{s.text}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </Section>

      {/* FEATURED MODELS */}
      {featured.length > 0 && (
        <Section className="border-t bg-card/50">
          <div className="page-container py-14 sm:py-20">
            <div className="flex items-end justify-between">
              <div>
                <h2 className="font-display text-2xl tracking-[-0.01em] sm:text-3xl">Featured looks</h2>
                <p className="mt-2 text-sm text-muted-foreground">Real people, ready to licence.</p>
              </div>
              <Link data-testid="landing-view-all-link" to="/explore" className="text-sm font-medium text-[hsl(174,38%,34%)] hover:underline">
                View all →
              </Link>
            </div>
            <div className="mt-8 grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4">
              {featured.map((m, i) => (
                <ModelCard key={m.id} model={m} index={i} />
              ))}
            </div>
          </div>
        </Section>
      )}

      {/* ETHICS PLEDGE */}
      <Section className="page-container py-14 sm:py-20">
        <Card className="rounded-2xl border-2 border-[hsl(174,38%,70%)]">
          <CardContent className="grid gap-8 p-8 sm:p-10 lg:grid-cols-2">
            <div className="space-y-4">
              <ShieldCheck className="h-8 w-8 text-[hsl(174,38%,34%)]" />
              <h2 className="font-display text-2xl tracking-[-0.01em] sm:text-3xl">The ethics are not optional</h2>
              <p className="text-sm text-muted-foreground">
                Every licence on LOOKBOOK is bound by a strict ethical agreement. Break it, and the licence is
                terminated without refund.
              </p>
              <EthicalAgreementDialog
                trigger={
                  <Button data-testid="landing-ethics-agreement-link" variant="outline" className="rounded-xl border bg-card hover:bg-muted">
                    Read the full agreement
                  </Button>
                }
              />
              <Button
                data-testid="landing-registry-link"
                variant="outline"
                className="ml-2 rounded-xl border bg-card hover:bg-muted"
                onClick={() => navigate("/verify")}
              >
                Verify a licence
              </Button>
            </div>
            <ul className="space-y-3 text-sm">
              {[
                "No X-rated or explicit modelling. Ever.",
                "No serious alterations — the model must remain recognisably themselves.",
                "Retouching only with the model's explicit consent, and only minor.",
                "Static images only — video licensing is not yet supported.",
                "Models set binding opt-outs (e.g. lingerie, alcohol) enforced automatically at screening.",
                "Every job AI-screened before it can reach a model.",
                "Usage rights expire — licences are strictly time-bound, and models can revoke their likeness.",
                "Models are paid the full fee upfront.",
                "Every licence publicly verifiable in the Ethical AI Registry.",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2.5">
                  <Handshake className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(174,38%,34%)]" />
                  {t}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </Section>

      {/* FOOTER */}
      <footer className="border-t">
        <div className="page-container flex flex-col items-start justify-between gap-4 py-10 sm:flex-row sm:items-center">
          <div>
            <p className="font-display text-lg tracking-[0.06em]">LOOKBOOK</p>
            <p className="text-xs text-muted-foreground">Ethical AI fashion modelling · consent-first image licensing · static imagery only</p>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted-foreground">
            <LegalDialog doc="model-terms" trigger={
              <button data-testid="footer-model-terms-link" className="hover:text-foreground hover:underline">Model Terms</button>
            } />
            <LegalDialog doc="brand-agreement" trigger={
              <button data-testid="footer-brand-agreement-link" className="hover:text-foreground hover:underline">Brand Licensing Agreement</button>
            } />
            <LegalDialog doc="privacy" trigger={
              <button data-testid="footer-privacy-link" className="hover:text-foreground hover:underline">Privacy & Data Security</button>
            } />
            <Link data-testid="footer-registry-link" to="/verify" className="hover:text-foreground hover:underline">Ethical AI Registry</Link>
            <span>© {new Date().getFullYear()} LOOKBOOK</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
